import type {
  ScrollAnchor,
  ScrollAnchorStrategy,
} from '../../types/scroll/scroll-anchor.types';

/**
 * Captures visible offsets using the consumer's ordered element/key selection.
 * @param container - Scroll viewport.
 * @param strategy - Content-independent access to rendered elements and stable keys.
 * @returns Visible anchors, without retaining application data.
 */
export const captureScrollAnchors = (
  container: HTMLDivElement,
  strategy: ScrollAnchorStrategy
): ScrollAnchor[] => {
  const viewport = container.getBoundingClientRect();
  const anchors: ScrollAnchor[] = [];
  for (const element of strategy.getElements(container)) {
    const rect = element.getBoundingClientRect();
    if (rect.bottom > viewport.top && rect.top < viewport.bottom) {
      anchors.push({
        element,
        key: strategy.getKey(element),
        offset: rect.top - viewport.top,
      });
    }
    if (rect.top >= viewport.bottom) break;
  }
  return anchors;
};

/**
 * Restores a surviving element's offset without double-applying native anchoring.
 * @param container - Scroll viewport.
 * @param anchors - Positions recorded before rendering or after user movement.
 * @param strategy - Resolves replacement elements by their stable keys.
 * @returns The requested offset before browser clamping, or undefined without an anchor.
 */
export const restoreScrollAnchor = (
  container: HTMLDivElement,
  anchors: readonly ScrollAnchor[],
  strategy: ScrollAnchorStrategy
): number | undefined => {
  const candidates = strategy.getElements(container);
  for (const anchor of anchors) {
    const element = container.contains(anchor.element)
      ? anchor.element
      : candidates.find(
          (candidate) =>
            anchor.key !== undefined &&
            strategy.getKey(candidate) === anchor.key
        );
    if (!element) continue;
    const offset =
      element.getBoundingClientRect().top -
      container.getBoundingClientRect().top;
    const requestedTop = container.scrollTop + offset - anchor.offset;
    container.scrollTop = requestedTop;
    return requestedTop;
  }
  return undefined;
};
